import { logout } from '@/features/auth';
import type { State } from '@/reducers/types';
import { DocumentSimStudioData } from '@/types';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import {
  createSimStudioDocument,
  deleteSimStudioDocument,
  getDocumentsSimStudioData,
  updateSimStudioDocument,
} from './actions';

export interface DocumentsSimStudioDataState {
  documentsSimStudioData: DocumentSimStudioData[] | null;
  isLoading: boolean;
}

const initialState: DocumentsSimStudioDataState = {
  documentsSimStudioData: null,
  isLoading: false,
};

export const { name, reducer } = createSlice({
  name: 'documentsSimStudioData',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDocumentsSimStudioData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDocumentsSimStudioData.fulfilled, (state, action) => {
        state.documentsSimStudioData = action.payload;
        state.isLoading = false;
      })
      .addCase(updateSimStudioDocument.fulfilled, (state, action) => {
        if (state.documentsSimStudioData) {
          const idxToUpdate = state.documentsSimStudioData.findIndex(
            (document) => document.id === action.payload.id,
          );
          if (idxToUpdate !== -1) {
            state.documentsSimStudioData[idxToUpdate] = action.payload;
          }
        }
      })
      .addCase(createSimStudioDocument.fulfilled, (state, action) => {
        if (state.documentsSimStudioData) {
          state.documentsSimStudioData.push(action.payload);
        } else {
          state.documentsSimStudioData = [action.payload];
        }
      })
      .addCase(deleteSimStudioDocument.fulfilled, (state, action) => {
        if (state.documentsSimStudioData) {
          state.documentsSimStudioData = state.documentsSimStudioData.filter(
            (document) => document.id !== action.payload,
          );
        }
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export const selectDocumentsSimStudioData = (state: State) => state[name].documentsSimStudioData;

export const selectDocumentsSimStudioIsLoading = (state: State) => state[name].isLoading;

export const makeSelectIsSimStudioDocumentEnabled = (id: string) =>
  createSelector([selectDocumentsSimStudioData], (documentsSimStudioData) => {
    const doc = documentsSimStudioData?.find((document) => document.id === id);
    return doc ? doc.enabled : false;
  });
